import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore as useAuthV1Store } from '@/Stores/AuthStore';
import { useAuthStore as useAuthV2Store } from '@/Stores/auth-v2-store';
import { newsReaderApi } from '../api/newsReaderApi';
import { NEWS_EVENTS_MAX_BATCH, NEWS_FEED_PAGE_SIZE } from '../constants/news.constants';
import type { NewsCategory, NewsEventInput, NewsPlatform } from '../types/news.types';

/** Type de compte v2 attendu sur chaque plateforme v2 (cf. `canAccessNewsPlatform` côté API). */
const V2_USER_TYPE: Record<Exclude<NewsPlatform, 'SAV'>, 'EXTERNAL' | 'INTERNAL'> = {
  PRO: 'EXTERNAL',
  UPSELL: 'INTERNAL',
  ADMIN: 'INTERNAL',
};

/**
 * Identifiant du lecteur sur la plateforme, ou `null` s'il n'y a personne à qui
 * montrer des nouveautés : session pas encore prête, non connecté, ou compte
 * que l'API refuserait de toute façon (un interne sur l'outil pro, par exemple).
 * Tant que c'est `null`, aucun appel ne part.
 *
 * L'identifiant entre dans les clés de cache et dans la clé de masquage du
 * bandeau : deux comptes qui se succèdent dans le même onglet ne partagent rien.
 */
export function useNewsViewerId(platform: NewsPlatform): string | null {
  const v2User = useAuthV2Store((state) =>
    state.isAuthReady && state.isAuthenticated ? state.currentUser : null,
  );
  const v1UserId = useAuthV1Store((state) =>
    state.access_token ? state.user?.uuid ?? null : null,
  );

  if (platform === 'SAV') return v1UserId;
  return v2User && v2User.type === V2_USER_TYPE[platform] ? v2User.id : null;
}

/**
 * Clés de cache de la lecture. Tout est rangé sous `['news-reader', plateforme,
 * lecteur]` — distinct de `['news-admin']`, l'écran de rédaction.
 */
export const newsReaderKeys = {
  summary: (platform: NewsPlatform, viewerId: string | null) =>
    ['news-reader', platform, viewerId, 'summary'] as const,
  feeds: (platform: NewsPlatform, viewerId: string | null) =>
    ['news-reader', platform, viewerId, 'feed'] as const,
  feed: (platform: NewsPlatform, viewerId: string | null, page: number, category?: NewsCategory) =>
    ['news-reader', platform, viewerId, 'feed', category ?? 'ALL', page] as const,
  detail: (platform: NewsPlatform, viewerId: string | null, slug: string) =>
    ['news-reader', platform, viewerId, 'detail', slug] as const,
};

/** Code HTTP d'une erreur axios, ou `undefined` pour une erreur réseau. */
export function getHttpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/** Une seule nouvelle tentative, et jamais sur un refus du serveur (401, 403, 404…). */
function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  const status = getHttpStatus(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

/** Nouvelles tentatives du résumé d'arrivée après le premier échec. */
const SUMMARY_MAX_RETRIES = 2;

/**
 * Résumé d'arrivée : badge, diapos de la modale, nouveautés du bandeau.
 *
 * Discret quand tout va bien : chargé UNE fois par arrivée (jamais périmé, pas
 * de rechargement au retour sur l'onglet, aucun rafraîchissement périodique),
 * puis rafraîchi seulement par invalidation, après un événement de suivi.
 *
 * Résilient quand ça va mal — une coupure de deux secondes au chargement ne doit
 * pas priver de badge, de modale et de bandeau jusqu'au rechargement complet :
 * - erreur réseau ou 5xx : deux nouvelles tentatives, à 2 s puis 4 s ;
 * - refus du serveur (4xx, dont 401/403) : définitif, aucune nouvelle tentative ;
 * - retour du réseau : relance UNIQUEMENT si la requête est en erreur. Une
 *   requête réussie n'est jamais relancée par la reconnexion.
 * (Hors ligne dès le départ, React Query met la requête en pause et la reprend
 * de lui-même au retour du réseau.)
 *
 * Aucune erreur n'est remontée : si les nouveautés restent en panne,
 * l'application s'affiche simplement sans badge, sans modale et sans bandeau.
 *
 * Une relance tardive ne peut ni rouvrir ni vider la modale : ses diapos sont
 * figées à l'ouverture et un verrou note qu'elle a été présentée (cf.
 * `NewsArrival`).
 */
export function useNewsSummary(platform: NewsPlatform) {
  const viewerId = useNewsViewerId(platform);
  return useQuery({
    queryKey: newsReaderKeys.summary(platform, viewerId),
    queryFn: () => newsReaderApi.getSummary(platform),
    enabled: !!viewerId,
    staleTime: Infinity,
    retry: (failureCount, error) => {
      const status = getHttpStatus(error);
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < SUMMARY_MAX_RETRIES;
    },
    retryDelay: (attempt) => 2000 * 2 ** attempt,
    refetchOnWindowFocus: false,
    // 'always' : sans cela une requête en erreur mais déjà pourvue de données
    // (rechargement raté après un événement) ne serait pas jugée périmée.
    refetchOnReconnect: (query) => (query.state.status === 'error' ? 'always' : false),
  });
}

/** Nombre de nouveautés non lues ; 0 tant qu'il n'est pas connu. */
export function useNewsUnreadCount(platform: NewsPlatform): number {
  const count = useNewsSummary(platform).data?.unreadCount;
  return typeof count === 'number' && count > 0 ? count : 0;
}

/** `category` : un seul type (onglet « Mises à jour ») ; absent, tout le fil. */
export function useNewsFeed(platform: NewsPlatform, page: number, category?: NewsCategory) {
  const viewerId = useNewsViewerId(platform);
  return useQuery({
    queryKey: newsReaderKeys.feed(platform, viewerId, page, category),
    queryFn: () => newsReaderApi.getFeed(platform, page, NEWS_FEED_PAGE_SIZE, category),
    enabled: !!viewerId,
    // Garde la page précédente affichée pendant le chargement de la suivante.
    keepPreviousData: true,
    retry: retryUnlessRefused,
  });
}

export function useNewsDetail(platform: NewsPlatform, slug: string | undefined) {
  const viewerId = useNewsViewerId(platform);
  return useQuery({
    queryKey: newsReaderKeys.detail(platform, viewerId, slug ?? ''),
    queryFn: () => newsReaderApi.getBySlug(platform, slug as string),
    enabled: !!viewerId && !!slug,
    retry: retryUnlessRefused,
  });
}

/**
 * Déclaration d'événements de suivi. Renvoie une fonction stable, à appeler
 * depuis un gestionnaire d'événement ou un effet d'affichage.
 *
 * - Silencieuse : un échec ne se voit pas (le suivi n'est pas une action de
 *   l'utilisateur). L'appel est idempotent côté serveur, il sera simplement
 *   refait à la prochaine occasion (modale représentée, page rouverte).
 * - `ignored` dans la réponse n'est pas une erreur (impersonation, nouveauté
 *   dépubliée entre-temps).
 * - Après un événement qui peut changer l'état « lu » ou la modale, le résumé
 *   (badge, bandeau) et le fil sont invalidés : ils se mettent à jour sans
 *   rechargement. Un simple clic sur le bouton d'action ne change rien à
 *   l'affichage : pas d'invalidation.
 */
export function useRecordNewsEvents(platform: NewsPlatform) {
  const viewerId = useNewsViewerId(platform);
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (events: NewsEventInput[]) => newsReaderApi.recordEvents(platform, events),
    onSuccess: (_response, events) => {
      const changesDisplay = events.some((event) =>
        event.types.some((type) => type !== 'cta_clicked'),
      );
      if (!changesDisplay) return;
      queryClient.invalidateQueries({ queryKey: newsReaderKeys.summary(platform, viewerId) });
      queryClient.invalidateQueries({ queryKey: newsReaderKeys.feeds(platform, viewerId) });
    },
  });

  return useCallback(
    (events: NewsEventInput[]) => {
      if (!viewerId || events.length === 0) return;
      // La modale compte 3 diapos au plus : la borne n'est là que par sécurité.
      mutate(events.slice(0, NEWS_EVENTS_MAX_BATCH));
    },
    [mutate, viewerId],
  );
}
